import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Table from "@/common/components/Table";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import { DataWrapper } from "@/common/components/dataWrapper";
import { PRODUCT_URL, INVENTORY_URL } from "@/common/constants";
import { KeyValueObj } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import useQuery from "@/hooks/useQuery";
import { batchTableHeaders, batchTableKeys } from "./subs/data";
import { BatchSerialModal } from "./subs/BatchSerialModal";
import { EditBatchModal } from "./subs/EditBatchModal";
import { ProductInventoryViewWrapper } from "./subs/style";

export interface IProductInventoryViewProps {}

// MED-652 stage 2: a single product's batch list, drilled into from the
// product-aggregated inventory page (add-inventory/index.tsx). Each batch
// row's own click opens the stage-3 serial-tree modal (view); Edit/Delete
// are the row actions relocated from the old flat per-batch table.
export function ProductInventoryView(_: IProductInventoryViewProps) {
  const { productId } = useParams();
  const apiCall = useApiCallHandling();

  const {
    data: product,
    loading: productLoading,
    error: productError,
  } = useQuery(`${PRODUCT_URL}/${productId}`);
  const {
    data: batches,
    loading: batchesLoading,
    error: batchesError,
    refetch: refetchBatches,
  } = useQuery<KeyValueObj[]>(`${PRODUCT_URL}/${productId}/batches`);

  const [selectedBatch, setSelectedBatch] = useState<KeyValueObj | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState<KeyValueObj | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState("");

  function openBatchModal(id: string | number) {
    const batch = (batches || []).find((b) => b.id === id);
    if (!batch) return;
    setSelectedBatch(batch);
    setShowBatchModal(true);
  }

  function openEditModal(id: string) {
    const batch = (batches || []).find((b) => b.id === id);
    if (!batch) return;
    setEditingBatch(batch);
    setShowEditModal(true);
  }

  function onBatchUpdated() {
    refetchBatches();
  }

  function onDeleteBatch(id: string) {
    setDeleteLoadingId(id);
    apiCall(
      `${INVENTORY_URL}/${id}`,
      "DELETE",
      {},
      () => refetchBatches(),
      () => {},
      () => setDeleteLoadingId(""),
    );
  }

  const tableData = useMemo(
    () =>
      (batches || []).map((batch) => ({
        batch_number: { value: batch.batch_number, type: "string" },
        manufacture_date: { value: batch.manufacture_date, type: "string" },
        expiry_date: { value: batch.expiry_date, type: "string" },
        quantity_in_stock: { value: batch.quantity_in_stock, type: "string" },
        edit: {
          type: "button",
          buttonType: "secondary",
          buttonName: "Edit",
          onClick: (_e: any, rowId: string) => openEditModal(rowId),
        },
        delete: {
          type: "button",
          buttonType: "danger",
          buttonName: "Delete",
          buttonLoading: deleteLoadingId === batch.id,
          onClick: (_e: any, rowId: string) => onDeleteBatch(rowId),
        },
        id: batch.id,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [batches, deleteLoadingId],
  );

  return (
    <ProductInventoryViewWrapper>
      <PageTitleNavs
        title="Product Inventory"
        backLink="/dashboard/inventories"
        bcrumbs={[
          { title: "Inventories", link: "/dashboard/inventories" },
          { title: product?.brand_name || "Product", link: "#" },
        ]}
      />

      <DataWrapper
        data={product}
        noDataMessage="Product not found"
        loading={productLoading}
        error={productError}
        loadingMessage="Loading product details..."
        failedMessage="Could not fetch product details."
      >
        <div className="product-header">
          <span className="brand">{product?.brand_name}</span>
          <span className="meta">
            SKU: {product?.sku} · {product?.form} {product?.strength}
            {product?.strength_unit}
          </span>
        </div>
      </DataWrapper>

      <div className="table-wrapper">
        <Table
          tableHeaders={batchTableHeaders}
          tableData={tableData}
          tableKeys={batchTableKeys}
          full
          rowLineColor="light"
          loading={batchesLoading}
          errorLoading={!!batchesError}
          clickable
          onTRClick={openBatchModal}
        />
      </div>

      <BatchSerialModal
        isOpen={showBatchModal}
        close={() => setShowBatchModal(false)}
        batch={selectedBatch}
      />
      <EditBatchModal
        isOpen={showEditModal}
        close={() => setShowEditModal(false)}
        batch={editingBatch}
        onUpdated={onBatchUpdated}
      />
    </ProductInventoryViewWrapper>
  );
}
